import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en/translation.json';
import cn from './locales/cn/translation.json';
import tw from './locales/tw/translation.json';
import ja from './locales/ja/translation.json';

// 语言码归一化：将各种写法统一到站内标准（ja/en/cn/tw）
const SUPPORTED_LANGS = ['ja', 'en', 'cn', 'tw'];
function normalizeLangLike(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  if (SUPPORTED_LANGS.includes(s)) return s;
  const lower = s.toLowerCase().replace('_', '-');
  if (lower === 'en' || lower.startsWith('en-')) return 'en';
  if (lower === 'ja' || lower.startsWith('ja-')) return 'ja';
  // 兼容旧写法：中文地区码/zh-* -> cn/tw
  if (lower === 'zh' || lower.startsWith('zh-') || lower === 'zh\u002dcn' || lower === 'zh\u002dtw') {
    if (lower.includes('tw') || lower.includes('hk') || lower.includes('hant')) return 'tw';
    return 'cn';
  }
  return null;
}

function resolveBrowserLang() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'en';
  try {
    const list = Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
    for (const item of list) {
      const normalized = normalizeLangLike(item);
      if (normalized) return normalized;
    }
  } catch (_) {}
  return 'en';
}

// 根路径（/）：当无历史选择时按浏览器语言选择，不在支持列表则回退英文
// 重要：要让 i18next-browser-languagedetector 按 order 调用它，必须 addDetector 注册。
const customRootPathDetector = {
  name: 'customRootPath',
  lookup() {
    // 只在浏览器端执行
    if (typeof window === 'undefined') return undefined;

    // 如果用户曾经手动选过语言（localStorage 已有值），不要覆盖用户选择
    try {
      const saved = window.localStorage && window.localStorage.getItem('i18nextLng');
      if (saved) return undefined;
    } catch (_) {}

    const pathname = window.location.pathname || '/';
    const segments = pathname.split('/').filter(Boolean);

    // 如果路径中有语言前缀，不处理（让 path 检测器处理）
    if (segments.length > 0 && ['ja', 'en', 'cn', 'tw'].includes(segments[0])) {
      return undefined;
    }

    // 根路径：默认日文
    if (pathname === '/' || pathname === '') {
      return resolveBrowserLang();
    }

    return undefined;
  },
  cacheUserLanguage() {
    // 让 LanguageDetector 自己处理 caches 配置
  }
};

// URL 语言前缀优先（强制归一化到 cn/tw，避免被 detector 降级成 zh 导致资源不命中）
const canonicalPathDetector = {
  name: 'canonicalPath',
  lookup() {
    if (typeof window === 'undefined') return undefined;
    try {
      const pathname = window.location.pathname || '/';
      const seg0 = pathname.split('/').filter(Boolean)[0] || '';
      const canonical = normalizeLangLike(seg0);
      return canonical || undefined;
    } catch (_) {
      return undefined;
    }
  },
  cacheUserLanguage() {}
};

const browserOrEnglishDetector = {
  name: 'browserOrEnglish',
  lookup() {
    if (typeof window === 'undefined') return undefined;
    return resolveBrowserLang();
  },
  cacheUserLanguage() {}
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(customRootPathDetector);
languageDetector.addDetector(canonicalPathDetector);
languageDetector.addDetector(browserOrEnglishDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      cn: { translation: cn },
      tw: { translation: tw },
      ja: { translation: ja }
    },
    // 只允许这些语言；旧写法会被 normalizeLangLike 归一化到 cn/tw
    supportedLngs: ['ja', 'en', 'cn', 'tw'],
    nonExplicitSupportedLngs: true,
    // 重要：必须按“当前语言码”加载，避免中文被降级为 zh 导致资源命中失败（进而回退到 ja）
    load: 'currentOnly',
    // 避免把 CN/TW 之类再做清理（我们使用短码）
    lowerCaseLng: false,
    // 日文为默认展示语言；缺失 key 时回退到英文
    fallbackLng: ['ja', 'en'],
    interpolation: { escapeValue: false },
    detection: {
      // URL 语言前缀优先：/cn 或 /tw 必须显示对应语言，避免“导航中文、内容日语”这类不一致
      // customRootPath：仅在“根路径且无历史选择”时按浏览器语言返回
      order: ['customRootPath', 'canonicalPath', 'localStorage', 'browserOrEnglish', 'htmlTag', 'cookie', 'subdomain'],
      caches: ['localStorage', 'cookie'],
      // localStorage 的键名
      lookupLocalStorage: 'i18nextLng'
    }
  });

export default i18n; 